import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import { apiGetBanners, apiGetPublicCoupons, apiGetSiteSection } from '../../api';
import { useToast } from '../../context/ToastContext';
import DeliveryAnnouncement from './DeliveryAnnouncement';
import { resolveBannerLink } from '../../utils/bannerLink';
import { getAssetImage, handleAssetImageError } from '../../utils/image';
import { updateBannerHeight } from '../../utils/carousel';

// Hide legacy internal titles generated for image-only banners.
const displayTitle = (title) =>
  /^(hero|store|promo) banner \d+$/i.test(String(title || '').trim()) ? '' : title;

const HeroBanner = () => {
  const { addToast } = useToast();
  const [bannerSlides, setBannerSlides] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [homeSettings, setHomeSettings] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      apiGetBanners('hero'),
      apiGetPublicCoupons(),
      apiGetSiteSection('home_settings'),
    ]).then(([bannerResult, couponResult, settingsResult]) => {
      if (!active) return;
      const banners = bannerResult.status === 'fulfilled' ? bannerResult.value.banners || [] : [];
      const slides = banners.map((b) => ({
        id: b.id,
        bg: b.bgColor,
        image: b.image,
        link: b.link,
        title: displayTitle(b.title),
        subtitle: b.subtitle,
        highlight: b.highlight,
        badge: b.badge,
      }));
      setBannerSlides(slides);
      if (couponResult.status === 'fulfilled') setCoupons(couponResult.value.coupons || []);
      if (settingsResult.status === 'fulfilled') setHomeSettings(settingsResult.value.data?.[0] || null);
      if ([bannerResult, couponResult, settingsResult].some((result) => result.status === 'rejected')) {
        addToast('Some promotional content could not be loaded', 'error');
      }
    });
    return () => {
      active = false;
    };
  }, [addToast]);

  return (
    <section className="carousel-section" aria-label="Promotional banners">
      {/* Main Banner Carousel */}
      {bannerSlides.length > 0 && <div className="relative flex min-w-0 flex-col">
        {bannerSlides.length > 1 && <div className="relative order-2 hidden justify-end items-center gap-2 px-3 py-2 md:flex sm:absolute sm:bottom-4 sm:right-4 sm:z-20 sm:p-0">
          <button
            ref={prevRef}
            className="w-11 h-11 rounded-full border border-gray-300 bg-white/90 flex items-center justify-center hover:bg-white transition"
            aria-label="Previous"
          >
            <FiChevronLeft className="text-lg text-black" />
          </button>
          <button
            ref={nextRef}
            className="w-11 h-11 rounded-full border border-gray-300 bg-white/90 flex items-center justify-center hover:bg-white transition"
            aria-label="Next"
          >
            <FiChevronRight className="text-lg text-black" />
          </button>
        </div>}
        <Swiper
          autoHeight
          watchOverflow
          grabCursor={bannerSlides.length > 1}
          modules={[Navigation, Autoplay]}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          autoplay={bannerSlides.length > 1 ? { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true } : false}
          loop={bannerSlides.length > 1}
          className="hero-swiper w-full min-w-0"
        >
          {bannerSlides.map((slide) => {
            const hasContent = slide.title || slide.subtitle || slide.highlight;
            const link = resolveBannerLink(slide.link);
            return (
              <SwiperSlide key={slide.id}>
                <article className="relative overflow-hidden" style={{ backgroundColor: slide.bg || undefined }}>
                  {link?.internal ? (
                    <Link to={link.to} className="block">
                      <img
                        src={getAssetImage(slide.image)}
                        onError={handleAssetImageError}
                        onLoad={updateBannerHeight}
                        alt={slide.title || 'Banner'}
                        className="block w-full h-auto object-contain"
                      />
                    </Link>
                  ) : link ? (
                    <a href={link.href} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={getAssetImage(slide.image)}
                        onError={handleAssetImageError}
                        onLoad={updateBannerHeight}
                        alt={slide.title || 'Banner'}
                        className="block w-full h-auto object-contain"
                      />
                    </a>
                  ) : (
                    <img
                      src={getAssetImage(slide.image)}
                      onError={handleAssetImageError}
                      onLoad={updateBannerHeight}
                      alt={slide.title || 'Banner'}
                      className="block w-full h-auto object-contain"
                    />
                  )}
                  {hasContent && (
                    <div className="pointer-events-none relative bg-black/80 py-4 sm:absolute sm:inset-0 sm:bg-black/40 sm:py-0 flex items-center">
                      <div className="w-full px-4 sm:px-10 md:px-16 text-white">
                        {slide.title && (
                          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold italic mb-2 drop-shadow-lg">
                            {slide.title}
                          </h2>
                        )}
                        {slide.subtitle && (
                          <p className="text-sm sm:text-lg md:text-xl mt-2 drop-shadow-md">
                            {slide.subtitle}
                          </p>
                        )}
                        {slide.highlight && (
                          <p className="text-orange-300 text-lg sm:text-2xl md:text-3xl font-bold italic mt-2 drop-shadow-lg">
                            {slide.highlight}
                          </p>
                        )}
                        {slide.badge && (
                          <p className="text-yellow-300 text-xs sm:text-sm mt-3 bg-green-700/80 inline-block px-3 py-1 rounded">
                            {slide.badge}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>}

      {/* Pre-order Banner */}
      <DeliveryAnnouncement coupons={coupons} settings={homeSettings} />
    </section>
  );
};

export default HeroBanner;
